import type { TextFieldProps } from '@mui/material'
import { TextField } from '@mui/material'
import type { ForwardedRef } from 'react'
import { forwardRef, useEffect, useState } from 'react'

export type IntegerTextFieldProps = {
  textFieldProps?: TextFieldProps
  initialValue?: string
  onValueChange: (value: number | undefined) => void
  min?: number
  max?: number
  error?: boolean
}

/**
 * Renders an integer-only text field component.
 *
 * Allows negative, zero, and positive integers. Supports min/max constraints.
 * Calls `onValueChange` when a valid value is updated.
 *
 * @param {IntegerTextFieldProps} props - The props for the integer text field.
 * @return {ReactNode} The integer text field component.
 */
const IntegerTextField = (
  { initialValue, error, ...props }: IntegerTextFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  // `initialValue` is declared `string`, but the Controller wrappers seed it straight from the form
  // value, which is a number for numeric fields. This is the only place it enters the component, so
  // coercing here keeps `stringValue` genuinely a string for every reader below (SUPP-4765).
  const [stringValue, setStringValue] = useState<string | undefined>(initialValue?.toString())
  const [isInvalidValue, setIsInvalidValue] = useState<boolean>(false)
  const [intValue, setIntValue] = useState<number | undefined>()

  // Matches optional minus sign followed by digits (no decimals)
  const validationRegex = /^-?\d*$/

  useEffect(() => {
    props.onValueChange(intValue)
  }, [intValue])

  useEffect(() => {
    validateAndUpdateIntValue(stringValue)
  }, [stringValue])

  const handleOnChange = (event: { target: { value: any } }) => {
    const value = event.target.value
    if (validationRegex.test(value)) {
      setStringValue(value)
    }
  }

  const handleOnBlur = () => {
    const parsedInt = validateAndUpdateIntValue(stringValue)
    setStringValue(parsedInt?.toString())
  }

  const meetsMinimum = (value: number) => {
    return props.min == undefined || value >= props.min
  }

  const meetsMaximum = (value: number) => {
    return props.max == undefined || value <= props.max
  }

  const validateAndUpdateIntValue = (value?: string): number | undefined => {
    // Whitespace-only is "no value", not invalid — same as FloatTextField. This `.trim()` is also
    // what makes the `initialValue?.toString()` coercion above load-bearing: drop one and the other
    // becomes dead code, which is how the SUPP-4765 crash reached production on the Float side.
    if (value == undefined || value.trim() === '') {
      setIsInvalidValue(false)
      setIntValue(undefined)
      return undefined
    }

    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed)) {
      setIsInvalidValue(true)
      setIntValue(undefined)
      return undefined
    }

    if (!meetsMinimum(parsed)) {
      setIsInvalidValue(true)
      setIntValue(props.min)
      return props.min
    }

    if (!meetsMaximum(parsed)) {
      setIsInvalidValue(true)
      setIntValue(props.max)
      return props.max
    }

    setIsInvalidValue(false)
    setIntValue(parsed)
    return parsed
  }

  return (
    <TextField
      {...props.textFieldProps}
      error={error || isInvalidValue}
      inputProps={{ ref, ...props.textFieldProps?.inputProps }}
      value={stringValue ?? ''}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
    />
  )
}

export default forwardRef(IntegerTextField)
